import { axiosInstance } from "@/lib/axiosInstance";

export interface ProfileType {
  id: string;
  college_id: string;
  full_name: string;
  org_email: string;
  personal_email: string | null;
  roll_number: string | null;
  phone: string | null;
  year_of_study: number | null;
  department: string | null;
  department_id: string | null;
  batch_id: string | null;
  graduation_year: number | null;
  bio: string | null;
  profile_photo_url: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  portfolio_url: string | null;
  preferred_locations: string[];
  preferred_domains: string[];
  is_open_to_work: boolean;
  is_active: boolean;
  is_eligible: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ProfileUpdateType {
  bio?: string;
  linkedin_url?: string;
  github_url?: string;
  portfolio_url?: string;
  preferred_locations?: string[];
  preferred_domains?: string[];
  is_open_to_work?: boolean;
  personal_email?: string;
  phone?: string;
  full_name?: string;
}

export interface ProfileContactUpdateType {
  org_email?: string;
  personal_email?: string;
  phone?: string;
}

export const fetchProfile = async (): Promise<ProfileType> => {
  const res = await axiosInstance.get("/v1/profile");
  return res.data?.data || res.data;
};

export const updateProfile = async (data: ProfileUpdateType): Promise<ProfileType> => {
  const res = await axiosInstance.put("/v1/profile", data);
  return res.data?.data || res.data;
};

export const updateProfileContact = async (data: ProfileContactUpdateType): Promise<any> => {
  const res = await axiosInstance.patch("/v1/profile/contact", data);
  return res.data;
};

export const updateProfilePhoto = async (file: File): Promise<ProfileType> => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await axiosInstance.post("/v1/profile/photo", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data?.data || res.data;
};

/**
 * Helper to reconstruct profile photo URL with specific size
 * @param basePath The base path from profile_photo_url (e.g. profile-photos/uuid)
 * @param size 'original' | 'thumb' | 'sm' | 'md' | 'lg' | 'xl'
 */
export const getProfilePhotoUrl = (path: string | null, size: 'original' | 'thumb' | 'sm' | 'md' | 'lg' | 'xl' = 'md') => {
  if (!path) return null;

  // If it's already a full URL or data URI, return as is
  if (path.startsWith('http') || path.startsWith('data:')) {
    return path;
  }

  const baseUrl = "https://pointz-files.s3.ap-south-1.amazonaws.com";
  const cleanBaseUrl = baseUrl.replace(/\/$/, ""); // Remove trailing slash
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  // If the path looks like it already has an extension, don't append the size folder
  if (path.split('/').pop()?.includes('.')) {
    return `${cleanBaseUrl}${cleanPath}?v=${Math.floor(Date.now() / 60000)}`;
  }

  // Otherwise, assume it's a base path that needs size.jpg
  return `${cleanBaseUrl}${cleanPath}/${size}.jpg?v=${Math.floor(Date.now() / 60000)}`;
};









// -- Skills --

export interface SkillType {
  id: string;
  student_id: string;
  name: string;
  proficiency: "beginner" | "intermediate" | "advanced" | "expert" | string;
  years_of_experience: number;
  created_at: string;
}

export interface SkillPayloadType {
  name: string;
  proficiency: "beginner" | "intermediate" | "advanced" | "expert" | string;
  years_of_experience: number;
}

export const fetchSkills = async (): Promise<SkillType[]> => {
  const res = await axiosInstance.get("/v1/profile/skills");
  // Check if backend wraps array inside a 'data' envelope
  return res.data?.data && Array.isArray(res.data.data) ? res.data.data : res.data;
};

export const addSkill = async (data: SkillPayloadType): Promise<SkillType> => {
  const res = await axiosInstance.post("/v1/profile/skills", data);
  return res.data;
};

export const updateSkill = async (id: string, data: SkillPayloadType): Promise<SkillType> => {
  const res = await axiosInstance.put(`/v1/profile/skills/${id}`, data);
  return res.data;
};

export const deleteSkill = async (id: string): Promise<{ message: string }> => {
  const res = await axiosInstance.delete(`/v1/profile/skills/${id}`);
  return res.data;
};

// -- Resumes --

export interface ResumeType {
  id: string;
  student_id: string;
  file_url: string;
  version: number;
  is_primary: boolean;
  uploaded_at: string;
}

export const fetchResumes = async (): Promise<ResumeType[]> => {
  const res = await axiosInstance.get("/v1/profile/resumes");
  return res.data?.data && Array.isArray(res.data.data) ? res.data.data : res.data;
};

export const uploadResume = async (file: File): Promise<ResumeType> => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await axiosInstance.post("/v1/profile/resumes", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
};

export const setPrimaryResume = async (id: string): Promise<{ message: string }> => {
  const res = await axiosInstance.patch(`/v1/profile/resumes/${id}/primary`);
  return res.data;
};

export const deleteResume = async (id: string): Promise<{ message: string }> => {
  const res = await axiosInstance.delete(`/v1/profile/resumes/${id}`);
  return res.data;
};
